import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import PortfolioManager from "@/pages/admin/PortfolioManager";
import JobsManager from "@/pages/admin/JobsManager";
import StatsPanel from "@/pages/admin/StatsPanel";

const LOGO = "/sito/img/logo.png";

export default function AdminDashboard() {
  const { user, ready, logout, updateSession } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("sezioni");
  const [content, setContent] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acc, setAcc] = useState({ current_password: "", new_email: "", new_password: "", confirm: "" });
  const [accNote, setAccNote] = useState({ cls: "", text: "" });
  const [accBusy, setAccBusy] = useState(false);
  const [smtp, setSmtp] = useState(null);
  const [smtpNote, setSmtpNote] = useState({ cls: "", text: "" });
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [candFilter, setCandFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [noteDraft, setNoteDraft] = useState({});
  const [noteSaved, setNoteSaved] = useState({});

  useEffect(() => { if (ready && !user) nav("/admin"); }, [ready, user, nav]);

  const loadPortfolio = useCallback(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data));
  }, []);

  const loadJobs = useCallback(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const setMsgStatus = async (id, status) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
    try {
      await api.put(`/messages/${id}/status`, { status });
    } catch {
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    }
  };

  const setMsgNote = async (id) => {
    const note = noteDraft[id] ?? "";
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, note } : m)));
    try {
      await api.put(`/messages/${id}/note`, { note });
      setNoteSaved((s) => ({ ...s, [id]: true }));
      setTimeout(() => setNoteSaved((s) => ({ ...s, [id]: false })), 1800);
    } catch {
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    }
  };

  const setMsgGiudizio = async (id, giudizio) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, giudizio } : m)));
    try {
      await api.put(`/messages/${id}/giudizio`, { giudizio });
    } catch {
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    }
  };

  useEffect(() => {
    if (!ready || !user) return;
    api.get("/content").then((r) => setContent(r.data));
    loadPortfolio();
    loadJobs();
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/smtp").then((r) => setSmtp({ ...r.data, password: "" })).catch(() => {});
  }, [ready, user, loadPortfolio, loadJobs]);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setContact = (k, v) => setContent((c) => ({ ...c, contacts: { ...c.contacts, [k]: v } }));
  const setCatItem = (ci, ii, k, v) =>
    setContent((c) => {
      const cats = c.service_categories.map((cat, idx) => {
        if (idx !== ci) return cat;
        const items = cat.items.map((it, j) => (j === ii ? { ...it, [k]: v } : it));
        return { ...cat, items };
      });
      return { ...c, service_categories: cats };
    });

  const saveContent = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/content", content);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert("Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  const saveSmtp = async () => {
    setSmtpNote({ cls: "", text: "" });
    setSmtpBusy(true);
    try {
      const body = { ...smtp };
      if (!body.password) delete body.password;
      await api.put("/admin/smtp", body);
      setSmtp((s) => ({ ...s, password: "", has_password: s.has_password || !!body.password }));
      setSmtpNote({ cls: "ok", text: "Impostazioni salvate." });
    } catch {
      setSmtpNote({ cls: "err", text: "Salvataggio non riuscito." });
    } finally {
      setSmtpBusy(false);
    }
  };

  const testSmtp = async () => {
    setSmtpNote({ cls: "", text: "Invio email di prova..." });
    setSmtpBusy(true);
    try {
      const { data } = await api.post("/admin/smtp/test");
      setSmtpNote({ cls: "ok", text: data.message });
    } catch (e) {
      const d = e.response?.data?.detail;
      setSmtpNote({ cls: "err", text: typeof d === "string" ? d : "Invio non riuscito." });
    } finally {
      setSmtpBusy(false);
    }
  };

  const listToText = (arr) => (arr || []).join("\n");
  const textToList = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);

  const saveCredentials = async () => {
    setAccNote({ cls: "", text: "" });
    if (!acc.current_password) { setAccNote({ cls: "err", text: "Inserisci la password attuale." }); return; }
    if (!acc.new_email && !acc.new_password) { setAccNote({ cls: "err", text: "Inserisci una nuova email o una nuova password." }); return; }
    if (acc.new_password && acc.new_password !== acc.confirm) { setAccNote({ cls: "err", text: "Le nuove password non coincidono." }); return; }
    setAccBusy(true);
    try {
      const { data } = await api.post("/auth/change-credentials", {
        current_password: acc.current_password,
        new_email: acc.new_email || null,
        new_password: acc.new_password || null,
      });
      updateSession(data.user, data.access_token);
      setAcc({ current_password: "", new_email: "", new_password: "", confirm: "" });
      setAccNote({ cls: "ok", text: "Credenziali aggiornate con successo." });
    } catch (e) {
      const d = e.response?.data?.detail;
      setAccNote({ cls: "err", text: typeof d === "string" ? d : "Aggiornamento non riuscito." });
    } finally {
      setAccBusy(false);
    }
  };

  if (!content) {
    return (
      <div className="adm"><div className="adm-wrap"><p className="lead">Caricamento…</p></div></div>
    );
  }

  const isApplication = (m) => (m.tipo || "").toLowerCase().includes("candidatura");
  const isSpontaneous = (m) => { const p = (m.posizione || "").toLowerCase(); return !p || p.includes("spontan"); };
  const applications = messages.filter(isApplication);
  const contacts = messages.filter((m) => !isApplication(m));
  const spontanee = applications.filter(isSpontaneous);
  const jobApps = applications.filter((m) => !isSpontaneous(m));
  const positions = [];
  jobs.filter((j) => j.is_open).forEach((j) => { if (!positions.includes(j.title)) positions.push(j.title); });
  jobApps.forEach((m) => { if (m.posizione && !positions.includes(m.posizione)) positions.push(m.posizione); });
  const candSel = candFilter || positions[0] || "__spont__";

  const STATUS_LABELS = { nuova: "Nuova", letta: "Letta", da_valutare: "Da valutare" };
  const STATUS_FILTERS = [["all", "Tutte"], ["nuova", "Nuove"], ["da_valutare", "Da valutare"], ["letta", "Lette"]];

  const renderStatusFilter = () => (
    <div className="msg-filter" data-testid="status-filter">
      <span className="msg-filter-label">Filtra per stato:</span>
      {STATUS_FILTERS.map(([k, l]) => (
        <button
          key={k}
          className={`msg-filter-btn${statusFilter === k ? " active" : ""}`}
          onClick={() => setStatusFilter(k)}
          data-testid={`status-filter-${k}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  const applyStatusFilter = (list) => (statusFilter === "all" ? list : list.filter((m) => (m.status || "nuova") === statusFilter));

  const GIUDIZIO_LABELS = { positivo: "Positivo", forse: "Forse", negativo: "Negativo" };
  const GIUDIZIO_BTNS = [["positivo", "👍 Positivo"], ["forse", "🤔 Forse"], ["negativo", "👎 Negativo"]];

  const renderMessages = (rawList, emptyText, testid, showGiudizio = false) => {
    const list = applyStatusFilter(rawList);
    return (
    <div data-testid={testid}>
      {list.length === 0 ? (
        <p className="adm-empty">{statusFilter === "all" ? emptyText : "Nessun elemento con questo stato."}</p>
      ) : (
        list.map((m) => {
          const st = m.status || "nuova";
          const gd = m.giudizio || "";
          const draft = noteDraft[m.id] ?? (m.note || "");
          return (
          <div className={`adm-msg status-${st}`} key={m.id} data-testid={`msg-${m.id}`}>
            <div className="head">
              <span><strong>{m.nome}</strong> · {m.email} {m.telefono ? `· ${m.telefono}` : ""}</span>
              <span className="head-right">
                {showGiudizio && gd && <span className={`msg-giudizio-badge ${gd}`} data-testid={`msg-giudizio-badge-${m.id}`}>{GIUDIZIO_LABELS[gd]}</span>}
                <span className={`msg-status-badge ${st}`} data-testid={`msg-status-${m.id}`}>{STATUS_LABELS[st]}</span>
                <span>{new Date(m.created_at).toLocaleString("it-IT")}</span>
              </span>
            </div>
            <div style={{ fontSize: ".8rem", color: "#3b56d6", fontWeight: 700, marginBottom: 6 }}>{m.tipo}{m.posizione ? ` — ${m.posizione}` : ""}{m.oggetto ? ` — ${m.oggetto}` : ""}{m.ruolo ? ` — ${m.ruolo}` : ""}</div>
            {m.residenza && <div style={{ fontSize: ".85rem", color: "#565a75", marginBottom: 6 }}>📍 Residenza: {m.residenza}</div>}
            {m.cv_path && <div style={{ marginBottom: 6 }}><a href={fileUrl(m.cv_path)} target="_blank" rel="noreferrer" className="adm-btn ghost sm" data-testid={`msg-cv-${m.id}`}>📄 Scarica CV{m.cv_name ? ` (${m.cv_name})` : ""}</a></div>}
            {m.messaggio && <div className="body">{m.messaggio}</div>}
            <div className="msg-actions">
              <button className={`adm-btn ghost sm ${st === "letta" ? "active" : ""}`} onClick={() => setMsgStatus(m.id, st === "letta" ? "nuova" : "letta")} data-testid={`msg-mark-letta-${m.id}`}>✓ Letta</button>
              <button className={`adm-btn ghost sm ${st === "da_valutare" ? "active" : ""}`} onClick={() => setMsgStatus(m.id, st === "da_valutare" ? "nuova" : "da_valutare")} data-testid={`msg-mark-valutare-${m.id}`}>★ Da valutare</button>
            </div>
            {showGiudizio && (
              <div className="msg-giudizio">
                <span className="msg-giudizio-label">Giudizio:</span>
                {GIUDIZIO_BTNS.map(([k, l]) => (
                  <button
                    key={k}
                    className={`msg-giudizio-btn ${k}${gd === k ? " active" : ""}`}
                    onClick={() => setMsgGiudizio(m.id, gd === k ? "" : k)}
                    data-testid={`msg-giudizio-${k}-${m.id}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
            <div className="msg-note">
              <label>📝 Nota interna (privata)</label>
              <textarea
                rows="2"
                value={draft}
                onChange={(e) => setNoteDraft((d) => ({ ...d, [m.id]: e.target.value }))}
                placeholder="Es. Colloquio fissato il 12/07, profilo interessante…"
                data-testid={`msg-note-input-${m.id}`}
              />
              <div className="msg-note-foot">
                <button className="adm-btn ghost sm" onClick={() => setMsgNote(m.id)} data-testid={`msg-note-save-${m.id}`}>Salva nota</button>
                {noteSaved[m.id] && <span className="msg-note-ok" data-testid={`msg-note-ok-${m.id}`}>Nota salvata ✓</span>}
              </div>
            </div>
          </div>
          );
        })
      )}
    </div>
    );
  };

  const titleMap = {
    sezioni: "Sezioni del sito",
    portfolio: "Portfolio",
    annunci: "Annunci di lavoro",
    recapiti: "Recapiti",
    statistiche: "Statistiche di traffico",
    candidature: "Candidature ricevute",
    contatti: "Messaggi di contatto",
    email: "Notifiche Email",
    account: "Account",
  };

  return (
    <div className="adm">
      <div className="adm-shell">
        <aside className="adm-sidebar">
          <img src={LOGO} className="adm-side-logo" alt="POWERCOM" />
          <nav className="adm-nav">
            {[["sezioni", "Sezioni"], ["portfolio", "Portfolio"], ["annunci", "Lavoro"], ["recapiti", "Recapiti"], ["statistiche", "Statistiche"], ["candidature", `Candidature (${applications.filter((m) => (m.status || "nuova") === "nuova").length})`], ["contatti", `Contatti (${contacts.filter((m) => (m.status || "nuova") === "nuova").length})`]].map(([k, l]) => (
              <button key={k} className={`adm-nav-item${tab === k ? " active" : ""}`} onClick={() => setTab(k)} data-testid={`tab-${k}`}>{l}</button>
            ))}
            <button className={`adm-nav-item${(tab === "email" || tab === "account") ? " active" : ""}`} onClick={() => setSettingsExpanded((v) => !v)} data-testid="tab-impostazioni">
              <span>Impostazioni</span>
              <span className="chev">{(settingsExpanded || tab === "email" || tab === "account") ? "▾" : "▸"}</span>
            </button>
            {(settingsExpanded || tab === "email" || tab === "account") && (
              <div className="adm-subnav">
                <button className={`adm-nav-sub${tab === "email" ? " active" : ""}`} onClick={() => setTab("email")} data-testid="tab-email">Notifiche Email</button>
                <button className={`adm-nav-sub${tab === "account" ? " active" : ""}`} onClick={() => setTab("account")} data-testid="tab-account">Account</button>
              </div>
            )}
          </nav>
          <div className="adm-side-foot">
            <span className="who">{user?.email}</span>
            <a className="adm-btn ghost sm" href="/" target="_blank" rel="noreferrer">Vedi sito ↗</a>
            <button className="adm-btn ghost sm" onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout">Esci</button>
          </div>
        </aside>

        <div className="adm-main">
          <div className="adm-wrap">
            <h1 className="title">{titleMap[tab] || "Backoffice"}</h1>

        {tab === "sezioni" && (
          <>
            <div className="adm-card">
              <h3>Hero (Home)</h3>
              <div className="adm-field"><label>Etichetta</label><input value={content.hero_eyebrow} onChange={(e) => set("hero_eyebrow", e.target.value)} data-testid="f-hero_eyebrow" /></div>
              <div className="adm-field"><label>Titolo</label><input value={content.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="f-hero_title" /></div>
              <div className="adm-field"><label>Sottotitolo</label><textarea rows="2" value={content.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="f-hero_subtitle" /></div>
            </div>

            <div className="adm-card">
              <h3>Chi Siamo</h3>
              <div className="adm-field"><label>Titolo</label><input value={content.about_title} onChange={(e) => set("about_title", e.target.value)} data-testid="f-about_title" /></div>
              <div className="adm-field"><label>Paragrafo 1</label><textarea rows="3" value={content.about_p1} onChange={(e) => set("about_p1", e.target.value)} /></div>
              <div className="adm-field"><label>Paragrafo 2</label><textarea rows="3" value={content.about_p2} onChange={(e) => set("about_p2", e.target.value)} /></div>
              <div className="adm-field"><label>Punti (uno per riga)</label><textarea rows="3" value={listToText(content.about_points)} onChange={(e) => set("about_points", textToList(e.target.value))} /></div>
            </div>

            <div className="adm-card">
              <h3>Introduzione Servizi</h3>
              <div className="adm-field"><label>Titolo</label><input value={content.services_intro_title} onChange={(e) => set("services_intro_title", e.target.value)} /></div>
              <div className="adm-field"><label>Testo</label><textarea rows="2" value={content.services_intro_text} onChange={(e) => set("services_intro_text", e.target.value)} /></div>
            </div>

            {(content.service_categories || []).map((cat, ci) => (
              <div className="adm-card" key={ci}>
                <h3>Servizi — {cat.name}</h3>
                {cat.items.map((it, ii) => (
                  <div className="adm-grid2" key={ii} style={{ marginBottom: 8 }}>
                    <div className="adm-field"><label>Voce {ii + 1} — Titolo</label><input value={it.title} onChange={(e) => setCatItem(ci, ii, "title", e.target.value)} /></div>
                    <div className="adm-field"><label>Descrizione</label><input value={it.desc} onChange={(e) => setCatItem(ci, ii, "desc", e.target.value)} /></div>
                  </div>
                ))}
              </div>
            ))}

            <div className="adm-card">
              <h3>Lavora con noi</h3>
              <div className="adm-field"><label>Titolo</label><input value={content.work_title} onChange={(e) => set("work_title", e.target.value)} /></div>
              <div className="adm-field"><label>Testo</label><textarea rows="3" value={content.work_text} onChange={(e) => set("work_text", e.target.value)} /></div>
              <div className="adm-field"><label>Punti (uno per riga)</label><textarea rows="3" value={listToText(content.work_points)} onChange={(e) => set("work_points", textToList(e.target.value))} /></div>
            </div>

            <div className="adm-card">
              <h3>Footer</h3>
              <div className="adm-field"><label>Descrizione</label><textarea rows="2" value={content.footer_desc} onChange={(e) => set("footer_desc", e.target.value)} /></div>
            </div>
          </>
        )}

        {tab === "recapiti" && (
          <div className="adm-card">
            <h3>Recapiti</h3>
            <div className="adm-field"><label>Ragione sociale</label><input value={content.contacts.company || ""} onChange={(e) => setContact("company", e.target.value)} data-testid="f-company" placeholder="POWERCOM s.r.l." /></div>
            <div className="adm-field"><label>Indirizzo / Sede</label><input value={content.contacts.address} onChange={(e) => setContact("address", e.target.value)} data-testid="f-address" /></div>
            <div className="adm-grid2">
              <div className="adm-field"><label>Telefono</label><input value={content.contacts.phone} onChange={(e) => setContact("phone", e.target.value)} data-testid="f-phone" /></div>
              <div className="adm-field"><label>Email</label><input value={content.contacts.email} onChange={(e) => setContact("email", e.target.value)} data-testid="f-email" /></div>
            </div>
            <div className="adm-grid2">
              <div className="adm-field"><label>P.IVA</label><input value={content.contacts.vat || ""} onChange={(e) => setContact("vat", e.target.value)} data-testid="f-vat" placeholder="10280401216" /></div>
              <div className="adm-field"><label>Orari</label><input value={content.contacts.hours} onChange={(e) => setContact("hours", e.target.value)} /></div>
            </div>
            <div className="adm-field"><label>Località mappa (Google Maps)</label><input value={content.contacts.map_query} onChange={(e) => setContact("map_query", e.target.value)} placeholder="Es. Via Bosco 71, Somma Vesuviana" /></div>
          </div>
        )}

        {tab === "portfolio" && <PortfolioManager items={portfolio} reload={loadPortfolio} />}

        {tab === "annunci" && <JobsManager items={jobs} reload={loadJobs} />}

        {tab === "statistiche" && <StatsPanel />}

        {tab === "candidature" && (
          <div>
            <div className="adm-chips" data-testid="cand-chips">
              {positions.map((p, i) => (
                <button key={p} className={`adm-chip${candSel === p ? " active" : ""}`} onClick={() => setCandFilter(p)} data-testid={`cand-chip-${i}`}>
                  {p} <span className="adm-chip-count">{jobApps.filter((m) => m.posizione === p).length}</span>
                </button>
              ))}
              <button className={`adm-chip${candSel === "__spont__" ? " active" : ""}`} onClick={() => setCandFilter("__spont__")} data-testid="cand-chip-spontanee">
                Candidature spontanee <span className="adm-chip-count">{spontanee.length}</span>
              </button>
            </div>
            {renderStatusFilter()}
            {candSel === "__spont__"
              ? renderMessages(spontanee, "Nessuna candidatura spontanea.", "candidature-spontanee-list", true)
              : renderMessages(jobApps.filter((m) => m.posizione === candSel), "Nessuna candidatura per questa posizione.", "candidature-list", true)}
          </div>
        )}

        {tab === "contatti" && (
          <div>
            {renderStatusFilter()}
            {renderMessages(contacts, "Nessun messaggio di contatto ricevuto.", "contatti-list")}
          </div>
        )}

        {tab === "email" && smtp && (
          <div className="adm-card" style={{ maxWidth: 620 }}>
            <h3>Notifiche Email (SMTP)</h3>
            <p style={{ color: "#565a75", fontSize: ".92rem", marginTop: -8, marginBottom: 18 }}>
              Configura il server SMTP per ricevere via email tutte le richieste dal sito (contatti e candidature).
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, fontWeight: 600, fontSize: ".92rem" }}>
              <input type="checkbox" checked={smtp.enabled} onChange={(e) => setSmtp({ ...smtp, enabled: e.target.checked })} data-testid="smtp-enabled" style={{ width: "auto" }} />
              Notifiche email attive
            </label>
            <div className="adm-grid2">
              <div className="adm-field"><label>Server SMTP (host)</label><input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="es. smtps.aruba.it" data-testid="smtp-host" /></div>
              <div className="adm-field"><label>Porta</label><input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} placeholder="587" data-testid="smtp-port" /></div>
            </div>
            <div className="adm-grid2">
              <div className="adm-field"><label>Sicurezza</label>
                <select value={smtp.security} onChange={(e) => setSmtp({ ...smtp, security: e.target.value })} data-testid="smtp-security">
                  <option value="tls">STARTTLS (porta 587)</option>
                  <option value="ssl">SSL/TLS (porta 465)</option>
                  <option value="none">Nessuna</option>
                </select>
              </div>
              <div className="adm-field"><label>Email destinatario</label><input value={smtp.to_email} onChange={(e) => setSmtp({ ...smtp, to_email: e.target.value })} placeholder="info@powercom.it" data-testid="smtp-to" /></div>
            </div>
            <div className="adm-grid2">
              <div className="adm-field"><label>Username</label><input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="info@powercom.it" data-testid="smtp-username" /></div>
              <div className="adm-field"><label>Password {smtp.has_password ? "(lascia vuoto per non modificare)" : ""}</label><input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.has_password ? "••••••••" : "password casella"} data-testid="smtp-password" /></div>
            </div>
            <div className="adm-grid2">
              <div className="adm-field"><label>Mittente (from email)</label><input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="info@powercom.it" data-testid="smtp-from" /></div>
              <div className="adm-field"><label>Nome mittente</label><input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="Sito POWERCOM" /></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
              <button className="adm-btn" onClick={saveSmtp} disabled={smtpBusy} data-testid="smtp-save-btn">Salva impostazioni</button>
              <button className="adm-btn ghost" onClick={testSmtp} disabled={smtpBusy} data-testid="smtp-test-btn">Invia email di prova</button>
              {smtpNote.text && <span className={`form-note ${smtpNote.cls}`} style={{ margin: 0, minHeight: 0 }} data-testid="smtp-note">{smtpNote.text}</span>}
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="adm-card" style={{ maxWidth: 560 }}>
            <h3>Credenziali di accesso</h3>
            <p style={{ color: "#565a75", fontSize: ".92rem", marginTop: -8, marginBottom: 18 }}>
              Modifica l'email e/o la password dell'amministratore. È richiesta la password attuale per confermare.
            </p>
            <div className="adm-field">
              <label>Password attuale *</label>
              <input type="password" value={acc.current_password} onChange={(e) => setAcc({ ...acc, current_password: e.target.value })} placeholder="••••••••" data-testid="acc-current-password" />
            </div>
            <div className="adm-field">
              <label>Nuova email</label>
              <input type="email" value={acc.new_email} onChange={(e) => setAcc({ ...acc, new_email: e.target.value })} placeholder={user?.email || "nuova@email.it"} data-testid="acc-new-email" />
            </div>
            <div className="adm-grid2">
              <div className="adm-field">
                <label>Nuova password</label>
                <input type="password" value={acc.new_password} onChange={(e) => setAcc({ ...acc, new_password: e.target.value })} placeholder="min. 6 caratteri" data-testid="acc-new-password" />
              </div>
              <div className="adm-field">
                <label>Conferma nuova password</label>
                <input type="password" value={acc.confirm} onChange={(e) => setAcc({ ...acc, confirm: e.target.value })} placeholder="ripeti password" data-testid="acc-confirm-password" />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
              <button className="adm-btn" onClick={saveCredentials} disabled={accBusy} data-testid="acc-save-btn">
                {accBusy ? "Salvataggio…" : "Aggiorna credenziali"}
              </button>
              {accNote.text && <span className={`form-note ${accNote.cls}`} style={{ margin: 0, minHeight: 0 }} data-testid="acc-note">{accNote.text}</span>}
            </div>
          </div>
        )}

      </div>

          {(tab === "sezioni" || tab === "recapiti") && (
            <div className="adm-save-bar">
              {saved && <span className="adm-save-note">✓ Modifiche salvate</span>}
              <button className="adm-btn" onClick={saveContent} disabled={saving} data-testid="save-content-btn">
                {saving ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
